import React, { useState, useMemo, useCallback } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio';
import moment from '@this/lib/moment';
import TemplateAssistantHotelOnlyFields from './TemplateAssistantHotelOnlyFields';
import TemplateAssistantTransportFields from './TemplateAssistantTransportFields';

type TripType = 'one-way' | 'round-trip' | 'hotel-only';

interface FormState {
  tripType: TripType;
  needsHotel: boolean;
  outwardDateTime: Date | null;
  departureLocation: string;
  arrivalLocation: string;
  returnDateTime: Date | null;
  hotelCheckinDate: Date | null;
  hotelCheckoutDate: Date | null;
  hotelArea: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onInsert: (text: string) => void;
}

const initialFormState: FormState = {
  tripType: 'one-way',
  needsHotel: false,
  outwardDateTime: null,
  departureLocation: '',
  arrivalLocation: '',
  returnDateTime: null,
  hotelCheckinDate: null,
  hotelCheckoutDate: null,
  hotelArea: ''
};

const TemplateAssistantModal = ({ open, onClose, onInsert }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [form, setForm] = useState<FormState>(initialFormState);

  const handleClose = useCallback(() => {
    setForm(initialFormState);
    onClose();
  }, [onClose]);

  const handleTripTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const tripType = event.target.value as TripType;
    setForm(prev => ({
      ...prev,
      tripType,
      needsHotel: tripType === 'hotel-only' ? false : prev.needsHotel,
      returnDateTime: tripType === 'one-way' || tripType === 'hotel-only' ? null : prev.returnDateTime
    }));
  }, []);

  const handleHotelChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const needsHotel = event.target.value === 'yes';
    setForm(prev => ({ ...prev, needsHotel }));
  }, []);

  const handleOutwardDateTimeChange = useCallback((date: Date | null) => {
    setForm(prev => ({ ...prev, outwardDateTime: date }));
  }, []);

  const handleReturnDateTimeChange = useCallback((date: Date | null) => {
    setForm(prev => ({ ...prev, returnDateTime: date }));
  }, []);

  const handleHotelCheckinDateChange = useCallback((date: Date | null) => {
    setForm(prev => ({
      ...prev,
      hotelCheckinDate: date,
      hotelCheckoutDate:
        prev.hotelCheckoutDate && date && prev.hotelCheckoutDate < date ? null : prev.hotelCheckoutDate
    }));
  }, []);

  const handleHotelCheckoutDateChange = useCallback((date: Date | null) => {
    setForm(prev => ({ ...prev, hotelCheckoutDate: date }));
  }, []);

  const handleHotelAreaChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const hotelArea = event.target.value;
    setForm(prev => ({ ...prev, hotelArea }));
  }, []);

  const handlePlaceChange = useCallback((field: 'origin' | 'destination', value: string) => {
    if (field === 'origin') {
      setForm(prev => ({ ...prev, departureLocation: value }));
    } else {
      setForm(prev => ({ ...prev, arrivalLocation: value }));
    }
  }, []);

  const formatDateTime = (date: Date): string => {
    return moment(date).format('YYYY年MM月DD日 HH:mm');
  };

  const formatDate = (date: Date): string => {
    return moment(date).format('YYYY年MM月DD日');
  };

  const generateText = useCallback((): string => {
    const lines: string[] = [];

    lines.push('出張提案依頼');
    lines.push(
      `旅程: ${form.tripType === 'one-way' ? '片道' : form.tripType === 'round-trip' ? '往復' : 'ホテルのみ'}`
    );

    if (form.tripType === 'hotel-only') {
      if (form.hotelCheckinDate) {
        lines.push(`チェックイン日: ${formatDate(form.hotelCheckinDate)}`);
      }

      if (form.hotelCheckoutDate) {
        lines.push(`チェックアウト日: ${formatDate(form.hotelCheckoutDate)}`);
      }

      if (form.hotelArea) {
        lines.push(`宿泊エリア: ${form.hotelArea}`);
      }

      return lines.join('\n');
    }

    lines.push(`ホテル: ${form.needsHotel ? '必要' : '不要'}`);

    if (form.outwardDateTime) {
      lines.push(`往路: ${formatDateTime(form.outwardDateTime)}`);
    }

    if (form.departureLocation) {
      lines.push(`出発地: ${form.departureLocation}`);
    }

    if (form.arrivalLocation) {
      lines.push(`到着地: ${form.arrivalLocation}`);
    }

    if (form.tripType === 'round-trip' && form.returnDateTime) {
      lines.push(`復路: ${formatDateTime(form.returnDateTime)}`);
    }

    return lines.join('\n');
  }, [form]);

  const canInsert =
    form.tripType === 'hotel-only'
      ? form.hotelCheckinDate !== null && form.hotelCheckoutDate !== null && form.hotelArea.trim() !== ''
      : form.outwardDateTime !== null &&
        form.departureLocation.trim() !== '' &&
        form.arrivalLocation.trim() !== '' &&
        (form.tripType === 'one-way' || form.returnDateTime !== null);

  const handleInsert = useCallback(() => {
    if (!canInsert) return;
    const text = generateText();
    onInsert(text);
    setForm(initialFormState);
  }, [canInsert, generateText, onInsert]);

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <ModalHeader>テンプレートアシスタント</ModalHeader>
      <ModalBody>
        <div style={styles.formContainer}>
          <div style={styles.formGroup}>
            <label style={styles.label}>旅程</label>
            <RadioGroup layout="horizontal" value={form.tripType} onChange={handleTripTypeChange}>
              <Radio value="one-way">片道</Radio>
              <Radio value="round-trip">往復</Radio>
              <Radio value="hotel-only">ホテルのみ</Radio>
            </RadioGroup>
          </div>

          {form.tripType === 'hotel-only' ? (
            <TemplateAssistantHotelOnlyFields
              hotelCheckinDate={form.hotelCheckinDate}
              hotelCheckoutDate={form.hotelCheckoutDate}
              hotelArea={form.hotelArea}
              onHotelCheckinDateChange={handleHotelCheckinDateChange}
              onHotelCheckoutDateChange={handleHotelCheckoutDateChange}
              onHotelAreaChange={handleHotelAreaChange}
            />
          ) : (
            <TemplateAssistantTransportFields
              tripType={form.tripType}
              needsHotel={form.needsHotel}
              outwardDateTime={form.outwardDateTime}
              departureLocation={form.departureLocation}
              arrivalLocation={form.arrivalLocation}
              returnDateTime={form.returnDateTime}
              onHotelChange={handleHotelChange}
              onOutwardDateTimeChange={handleOutwardDateTimeChange}
              onReturnDateTimeChange={handleReturnDateTimeChange}
              onPlaceChange={handlePlaceChange}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="sub" onClick={handleClose}>
          キャンセル
        </Button>
        <Button onClick={handleInsert} disabled={!canInsert}>
          挿入
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const getStyles = (theme: AITTheme) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 600 as const,
    color: theme.textColor
  }
});

export default TemplateAssistantModal;
